/**
 * Empty — Placeholder shown when a list, search, or container has no content.
 *
 * Empty Component Guidelines
 * - Use Empty whenever a data-driven area can be blank (search results, tables,
 *   dashboards, combobox dropdowns). An empty state reassures users and suggests
 *   next steps.
 * - Use `size="sm"` inside dropdowns, popovers, or compact panels; `size="base"`
 *   for full-page or full-section empty states.
 * - Use `icon` for generic empty states with an icon indicator. Use `media` when
 *   you have a branded illustration or custom graphic to show instead.
 * - Prefer the simple `<Empty>` for standard icon + title + description layouts.
 *   Switch to the composable API (`EmptyRoot` / `EmptyGraphic` / `EmptyTitle` /
 *   etc.) for custom layouts or when embedding interactive content.
 * @see https://ui.shadcn.com/docs/components/empty shadcn/ui Empty documentation
 */
import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { cva } from "class-variance-authority";
import { FolderSearch } from "lucide-react";

const emptyVariants = cva("flex flex-col items-center justify-center", {
  variants: {
    size: {
      base: "gap-4",
      sm: "gap-3",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

const emptyTitleVariants = cva("text-center text-default font-semibold", {
  variants: {
    size: {
      base: "text-base",
      sm: "text-sm",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

const emptyDescriptionVariants = cva("text-center text-muted", {
  variants: {
    size: {
      base: "text-base",
      sm: "text-sm",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

type EmptySize = "base" | "sm";

const EmptySizeContext = React.createContext<EmptySize>("base");

type EmptyRootProps<ClassName extends string = string> =
  React.PropsWithChildren<
    VariantProps<typeof emptyVariants> & {
      className?: LayoutClassName<ClassName>;
      UNSAFE_className?: string;
    }
  >;

const EmptyRootInner = <ClassName extends string>(
  {
    children,
    size = "base",
    className,
    UNSAFE_className,
  }: EmptyRootProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  return (
    <EmptySizeContext.Provider value={size ?? "base"}>
      <div
        ref={ref}
        data-slot="empty"
        className={twMerge(
          emptyVariants({ size }),
          className,
          UNSAFE_className,
        )}
      >
        {children}
      </div>
    </EmptySizeContext.Provider>
  );
};

const EmptyRoot = React.forwardRef(EmptyRootInner) as <
  ClassName extends string,
>(
  props: EmptyRootProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

type EmptyHeaderProps<ClassName extends string = string> =
  React.PropsWithChildren<{
    className?: LayoutClassName<ClassName>;
    UNSAFE_className?: string;
  }>;

function EmptyHeader<ClassName extends string>({
  children,
  className,
  UNSAFE_className,
}: EmptyHeaderProps<ClassName>) {
  return (
    <div
      data-slot="empty-header"
      className={twMerge(
        "flex flex-col items-center gap-2",
        className,
        UNSAFE_className,
      )}
    >
      {children}
    </div>
  );
}

type EmptyTitleProps<ClassName extends string = string> =
  React.PropsWithChildren<{
    className?: LayoutClassName<ClassName>;
    UNSAFE_className?: string;
  }>;

function EmptyTitle<ClassName extends string>({
  children,
  className,
  UNSAFE_className,
}: EmptyTitleProps<ClassName>) {
  const size = React.useContext(EmptySizeContext);
  return (
    <h3
      data-slot="empty-title"
      className={twMerge(
        emptyTitleVariants({ size }),
        className,
        UNSAFE_className,
      )}
    >
      {children}
    </h3>
  );
}

type EmptyDescriptionProps<ClassName extends string = string> =
  React.PropsWithChildren<{
    className?: LayoutClassName<ClassName>;
    UNSAFE_className?: string;
  }>;

function EmptyDescription<ClassName extends string>({
  children,
  className,
  UNSAFE_className,
}: EmptyDescriptionProps<ClassName>) {
  const size = React.useContext(EmptySizeContext);
  return (
    <p
      data-slot="empty-description"
      className={twMerge(
        emptyDescriptionVariants({ size }),
        className,
        UNSAFE_className,
      )}
    >
      {children}
    </p>
  );
}

type EmptyContentProps<ClassName extends string = string> =
  React.PropsWithChildren<{
    className?: LayoutClassName<ClassName>;
    UNSAFE_className?: string;
  }>;

function EmptyContent<ClassName extends string>({
  children,
  className,
  UNSAFE_className,
}: EmptyContentProps<ClassName>) {
  return (
    <div
      data-slot="empty-content"
      className={twMerge(
        "flex flex-col items-center gap-2",
        className,
        UNSAFE_className,
      )}
    >
      {children}
    </div>
  );
}

function EmptyGraphic({
  size = "base",
  icon,
}: {
  size?: EmptySize;
  icon?: React.ReactNode;
}) {
  const iconContainerSize = size === "base" ? "size-16" : "size-12";
  const iconSize = size === "base" ? 24 : 14;
  const showRings = size === "base";
  const outerRingSize = 218;
  const innerRingSize = 149;
  const ringClipHeight = 150;
  const ringClipBottom = outerRingSize - ringClipHeight;

  const defaultIcon = <FolderSearch size={iconSize} />;

  return (
    <div className="relative flex items-center justify-center">
      {showRings && (
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: outerRingSize,
            height: outerRingSize,
            clipPath: `inset(0px 0px ${ringClipBottom}px 0px)`,
          }}
        >
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-dashed border-border"
            style={{ width: innerRingSize, height: innerRingSize }}
          />
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-[1.5px] border-dashed border-border"
            style={{ width: outerRingSize, height: outerRingSize }}
          />
        </div>
      )}
      <div
        className={twMerge(
          "relative z-10 flex items-center justify-center rounded-full bg-sky-soft border border-sky-border text-primary",
          iconContainerSize,
        )}
      >
        {icon ?? defaultIcon}
      </div>
    </div>
  );
}

type EmptyBaseProps<ClassName extends string = string> =
  React.PropsWithChildren<{
    title: string;
    description?: string;
    size?: EmptySize;
    className?: LayoutClassName<ClassName>;
    UNSAFE_className?: string;
  }>;

type EmptyWithIcon<ClassName extends string = string> =
  EmptyBaseProps<ClassName> & {
    icon?: React.ReactNode;
    media?: never;
  };

type EmptyWithMedia<ClassName extends string = string> =
  EmptyBaseProps<ClassName> & {
    icon?: never;
    media?: React.ReactNode;
  };

export type EmptyProps<ClassName extends string = string> =
  | EmptyWithIcon<ClassName>
  | EmptyWithMedia<ClassName>;

const EmptyInner = <ClassName extends string>(
  {
    title,
    description,
    size = "base",
    icon,
    media,
    className,
    UNSAFE_className,
    children,
  }: EmptyProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const renderMedia = () => {
    if (media !== undefined) {
      return <div className="flex items-center justify-center">{media}</div>;
    }
    return <EmptyGraphic size={size} icon={icon} />;
  };

  return (
    <EmptyRoot
      ref={ref}
      size={size}
      className={className}
      UNSAFE_className={UNSAFE_className}
    >
      {renderMedia()}
      <div className="flex flex-col items-center gap-1">
        <EmptyTitle>{title}</EmptyTitle>
        {description && <EmptyDescription>{description}</EmptyDescription>}
      </div>
      {children && <EmptyContent>{children}</EmptyContent>}
    </EmptyRoot>
  );
};

const Empty = React.forwardRef(EmptyInner) as <ClassName extends string>(
  props: EmptyProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

export {
  Empty,
  EmptyRoot,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
  EmptyGraphic,
};
